import { useMemo } from "react";
import * as dateFNS from "date-fns";
import ReactHTMLParser, { convertNodeToElement } from "react-html-parser";
import DataTable from "../../../../../../components/DataTable";
import { DoubtContainer, Link, Status, TutorAvatar } from "./styles";
import { smallDateFormatWithTime } from "../../../../../../utils/Utils";

/**@type {import("../../../../../../components/DataTable/interfaces").TableColumn[]} */
const columns = [
  { key: "name", name: "Nome/Curso", isSingleLine: true },
  { key: "doubt", name: "Dúvida", maxWidth: "50rem" },
  {
    key: "activity_at",
    name: "Primeira/Última atividade em",
    isSingleLine: true,
    align: "center",
  },
  { key: "status", name: "Status", align: "center", align: "center" },
  { key: "answered_by", name: "Responsável", align: "center" },
];

const statusMapper = {
  open: "Aberta",
  closed: "Respondida",
};

const statusColor = {
  open: "#000",
  closed: "#28a745",
};

/**@type {import("../../../../../../components/DataTable/interfaces").TableFilterComponentOption[]} */
const statusFilterOptions = [
  { key: "all", name: "Todos", description: "Buscar todas as dúvidas" },
  { key: "open", name: "Abertas", description: "Busca as dúvidas em aberto" },
  {
    key: "closed",
    name: "Respondidas",
    description: "Busca as dúvidas respondidas",
  },
];

/** @type {import("react-html-parser").Transform} */
const transformDoubtHtml = (node, index) => {
  if (node.type === "tag") {
    const previousStyle = node.attribs?.style
      ? node.attribs.style.split(";")
      : [];
    const newStyle = [
      ...previousStyle,
      "font-size: 0.9rem",
      "font-family: Montserrat",
    ];

    node.attribs = {
      style: newStyle.join(";"),
    };

    return convertNodeToElement(node, index, transformDoubtHtml);
  }
};

/**
 *
 * @param {object} props
 * @param {import("../../interfaces").DoubtsData[]} props.data
 * @param {import("../../interfaces").DoubtsPaging} props.paging
 * @param {boolean} props.isLoading
 * @param {number} props.page
 * @param {object} props.selectedFilters
 * @param {(filters) => void} props.onSubmitFilters
 * @param {() => void} props.onResetFilters
 * @param {(newPage) => void} props.onChangePage
 * @returns
 */
export default function DoubsTable(props) {
  const rows = useMemo(() => {
    if (!props.data) {
      return [];
    }

    return props.data.map((item) => {
      /**@type {import("../../../../../../components/DataTable/interfaces").TableRow} */
      const row = {
        name: (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <Link
              title={`[${item.user.id}] ${item.user.email}`}
              href={`/admin/alunos/detalhes/${item.user.id}`}
              target="_blank"
              style={{ fontWeight: 500 }}
            >
              {item.user.name}
            </Link>
            <Link
              title={`[${item.course.id}] ${item.course.name}`}
              href={`/online/assistir/${item.course.id}`}
              target="_blank"
            >
              {item.course.name}
            </Link>
          </div>
        ),
        doubt: (
          <Link
            title="Primeira pergunta da dúvida"
            href={`/online/tickets/${item.id}`}
            target="_blank"
          >
            <DoubtContainer>
              {ReactHTMLParser(item.first_message, {
                transform: transformDoubtHtml,
              })}
            </DoubtContainer>
          </Link>
        ),
        activity_at: (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <span
              title="Data da primeira pergunta enviada"
              style={{ fontWeight: 500 }}
            >
              {smallDateFormatWithTime(
                new Date(
                  new Date(item.first_activity_at).getTime() +
                    3 * 60 * 60 * 1000
                )
              )}
            </span>
            <span title="Data da última atividade na dúvida, pode ser resposta ou pergunta nova do aluno">
              {smallDateFormatWithTime(
                new Date(
                  new Date(item.last_activity_at).getTime() + 3 * 60 * 60 * 1000
                )
              )}
            </span>
          </div>
        ),
        status: (
          <Status
            title="Status da dúvida, atualiza automaticamente quando a dúvida é respondida ou tem uma nova pergunta"
            color={statusColor[item.status]}
          >
            {statusMapper[item.status]}
          </Status>
        ),
        answered_by: (
          <TutorAvatar
            src={item.tutor.photoUrl}
            title={`Responsável atual pela dúvida: ${item.tutor.name}`}
          />
        ),
      };

      return row;
    });
  }, [props.data]);

  const filters = useMemo(() => {
    /**@type {import("../../../../../../components/DataTable/interfaces").TableFilter[]} */
    const filters = [
      { key: "user", name: "Aluno", component: { type: "text" } },
      { key: "doubt", name: "Termo da mensagem", component: { type: "text" } },
      {
        key: "status",
        name: "Status",
        component: {
          type: "select",
          options: statusFilterOptions,
          defaultValue: statusFilterOptions[0].key,
        },
      },
      {
        key: "startDate",
        name: "Período inicial",
        component: { type: "date" },
      },
      { key: "endDate", name: "Período final", component: { type: "date" } },
    ];

    return filters;
  }, []);

  return (
    <DataTable
      hideRowsPerPage
      hasStickHeader
      showFilters
      isDense={false}
      maxHeight="70vh"
      rowsPerPage={50}
      isLoading={props.isLoading}
      columns={columns}
      rows={rows}
      filters={filters}
      selectedFilters={props.selectedFilters}
      page={props.page || 0}
      totalRecords={props.paging.total}
      onPageChange={props.onChangePage}
      onSubmitFilters={props.onSubmitFilters}
      onResetFilters={props.onResetFilters}
    />
  );
}
